import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { getMember } from '../services/api';
import WIA from '../components/WIA';
import EventsSection from '../components/EventsSection';
import ChatSection from '../components/ChatSection';
import PaymentsSection from '../components/PaymentsSection';
import WBusiness from '../components/WBusiness';
import Wcash from '../components/Wcash';
import WPartners from '../components/WPartners';
import '../styles/wclub-effects.css';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MemberView = ({ memberId }) => {
  const [member, setMember] = useState(null);
  const [activeTab, setActiveTab] = useState('card');
  const [loading, setLoading] = useState(true);
  const [flipped, setFlipped] = useState(false);
  const [xVisible, setXVisible] = useState(false);
  const [wcashBalance, setWcashBalance] = useState(null);
  const [showWcashBalance, setShowWcashBalance] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);

  useEffect(() => { loadMember(); }, [memberId]);
  
  // Load Wcash balance
  useEffect(() => {
    if (!member) return;
    const loadBalance = async () => {
      try {
        const res = await axios.get(`${API}/wallet/${member.id}/balance`);
        setWcashBalance(res.data?.wcash_balance || 0);
      } catch (e) {
        setWcashBalance(0);
      }
    };
    loadBalance();
  }, [member]);

  // Dynamic manifest: update the manifest link to point to backend with member ID
  useEffect(() => {
    if (!member) return;
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const manifestUrl = `${backendUrl}/api/manifest.json?member=${memberId}`;
    let link = document.querySelector('link[rel="manifest"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'manifest';
      document.head.appendChild(link);
    }
    const origHref = link.href;
    link.href = manifestUrl;
    // Also set iOS-specific meta for apple-mobile-web-app
    let appleTitle = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    if (!appleTitle) {
      appleTitle = document.createElement('meta');
      appleTitle.name = 'apple-mobile-web-app-title';
      document.head.appendChild(appleTitle);
    }
    appleTitle.content = member.name.split(' ')[0];
    return () => {
      if (link) link.href = origHref;
    };
  }, [member, memberId]);

  const loadMember = async () => {
    try {
      setLoading(true);
      const response = await getMember(memberId);
      setMember(response.data);
    } catch (error) {
      setMember(null);
    } finally {
      setLoading(false);
    }
  };

  const isCardTab = activeTab === 'card';

  useEffect(() => {
    let timer;
    if (!isCardTab) {
      setXVisible(true);
      timer = setTimeout(() => setXVisible(false), 3500);
    } else { setXVisible(false); }
    return () => clearTimeout(timer);
  }, [activeTab, isCardTab]);

  const showXBriefly = () => {
    setXVisible(true);
    setTimeout(() => setXVisible(false), 3500);
  };

  const getCategoryColor = (cat) => {
    switch (cat) {
      case 'diamond': return '#b9f2ff';
      case 'black': return '#a0a0a0';
      default: return '#c0c0c0';
    }
  };

  const tabs = [
    { id: 'card', label: 'Cartão', icon: 'card' },
    { id: 'wia', label: 'W IA', icon: 'ai' },
    { id: 'events', label: 'Eventos', icon: 'calendar' },
    { id: 'chat', label: 'Chat', icon: 'chat' },
    { id: 'wcash', label: 'Wcash', icon: 'wallet' },
    { id: 'payments', label: 'Pagtos', icon: 'pay' },
    { id: 'wbusiness', label: 'Business', icon: 'business' },
    { id: 'wpartners', label: 'Partners', icon: 'globe' },
    { id: 'config', label: 'Config', icon: 'settings' },
  ];

  // Icon component with stroke/glow effects
  const MenuIcon = ({ icon, isActive }) => {
    const iconStyle = {
      filter: isActive ? 'drop-shadow(0 0 10px rgba(59,130,246,0.9)) drop-shadow(0 0 20px rgba(59,130,246,0.5))' : 'none',
      transition: 'all 0.3s ease',
    };
    
    const strokeColor = isActive ? '#3b82f6' : 'rgba(255,255,255,0.85)';
    const strokeWidth = isActive ? 2 : 1.5;
    
    const icons = {
      card: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke={strokeColor} strokeWidth={strokeWidth} style={iconStyle}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
        </svg>
      ),
      ai: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke={strokeColor} strokeWidth={strokeWidth} style={iconStyle}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z" />
        </svg>
      ),
      calendar: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke={strokeColor} strokeWidth={strokeWidth} style={iconStyle}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
        </svg>
      ),
      chat: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke={strokeColor} strokeWidth={strokeWidth} style={iconStyle}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z" />
        </svg>
      ),
      wallet: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke={strokeColor} strokeWidth={strokeWidth} style={iconStyle}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a2.25 2.25 0 00-2.25-2.25H15a3 3 0 11-6 0H5.25A2.25 2.25 0 003 12m18 0v6a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 18v-6m18 0V9M3 12V9m18 0a2.25 2.25 0 00-2.25-2.25H5.25A2.25 2.25 0 003 9m18 0V6a2.25 2.25 0 00-2.25-2.25H5.25A2.25 2.25 0 003 6v3" />
        </svg>
      ),
      pay: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke={strokeColor} strokeWidth={strokeWidth} style={iconStyle}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z" />
        </svg>
      ),
      business: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke={strokeColor} strokeWidth={strokeWidth} style={iconStyle}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 14.15v4.25c0 1.094-.787 2.036-1.872 2.18-2.087.277-4.216.42-6.378.42s-4.291-.143-6.378-.42c-1.085-.144-1.872-1.086-1.872-2.18v-4.25m16.5 0a2.18 2.18 0 00.75-1.661V8.706c0-1.081-.768-2.015-1.837-2.175a48.114 48.114 0 00-3.413-.387m4.5 8.006c-.194.165-.42.295-.673.38A23.978 23.978 0 0112 15.75c-2.648 0-5.195-.429-7.577-1.22a2.016 2.016 0 01-.673-.38m0 0A2.18 2.18 0 013 12.489V8.706c0-1.081.768-2.015 1.837-2.175a48.111 48.111 0 013.413-.387m7.5 0V5.25A2.25 2.25 0 0013.5 3h-3a2.25 2.25 0 00-2.25 2.25v.894m7.5 0a48.667 48.667 0 00-7.5 0M12 12.75h.008v.008H12v-.008z" />
        </svg>
      ),
      globe: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke={strokeColor} strokeWidth={strokeWidth} style={iconStyle}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" />
        </svg>
      ),
      settings: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke={strokeColor} strokeWidth={strokeWidth} style={iconStyle}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
    };
    
    return icons[icon] || icons.card;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="flex gap-1">
          {[0, 1, 2].map(i => (
            <motion.span key={i} className="w-2 h-2 rounded-full bg-white/20"
              animate={{ opacity: [0.2, 0.8, 0.2] }}
              transition={{ duration: 1, repeat: Infinity, delay: i * 0.3 }} />
          ))}
        </div>
      </div>
    );
  }

  if (!member) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <p className="text-white/30 text-sm font-['Jost']">Membro nao encontrado</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      {!isCardTab && (
        <div className="fixed top-0 left-0 right-0 z-50"
          style={{ paddingTop: 'max(env(safe-area-inset-top, 0px), 8px)' }}
          onClick={showXBriefly}>
          <AnimatePresence>
            {xVisible && (
              <motion.button initial={{ opacity: 0 }} animate={{ opacity: 0.7 }} exit={{ opacity: 0 }}
                onClick={(e) => { e.stopPropagation(); setActiveTab('card'); }}
                className="absolute left-4 w-8 h-8 flex items-center justify-center touch-manipulation z-50"
                style={{ top: 'max(calc(env(safe-area-inset-top, 0px) + 8px), 16px)' }}
                data-testid="member-back-btn">
                <svg className="w-5 h-5 text-white/70" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
              </motion.button>
            )}
          </AnimatePresence>
          <div className="h-8" />
        </div>
      )}

      {isCardTab && (
        <header className="border-b border-white/[0.06] bg-black/90 backdrop-blur-lg"
          style={{ paddingTop: 'max(env(safe-area-inset-top, 0px), 12px)' }}>
          <div className="container mx-auto px-4 py-2.5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img src="/logo-wclub-large.png" alt="WClub" className="w-10 h-10" />
                <div className="wclub-logo-container">
                  <h1 className="wclub-logo text-xl font-['Jost'] font-extralight tracking-[0.3em] uppercase">WCLUB</h1>
                  <div className="wclub-loading-bar"></div>
                  <p className="text-[9px] text-white/25 mt-0.5 tracking-widest font-['Jost']">Cartao Digital</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-[10px] text-white/25">Bem-vindo(a)</p>
                <p className="text-xs font-medium text-white/50">{member.name}</p>
              </div>
            </div>
          </div>
        </header>
      )}

      <div className={`container mx-auto ${activeTab === 'wia' || activeTab === 'chat' ? 'px-0' : 'px-3'}`}
        style={!isCardTab ? { paddingTop: activeTab === 'wia' ? '0' : 'max(calc(env(safe-area-inset-top, 0px) + 40px), 50px)' } : { padding: '1.5rem 0.75rem' }}>
        <motion.div 
          key={activeTab} 
          initial={{ opacity: 0, y: 10 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ duration: 0.2 }}
          className={activeTab === 'wia' ? 'fixed inset-0 z-40 bg-black' : ''}
          style={activeTab === 'wia' ? { paddingTop: 'env(safe-area-inset-top, 0px)' } : {}}
        >
          {activeTab === 'card' && (
            <div className="max-w-sm mx-auto px-4">
              {/* CSS for animated border */}
              <style>{`
                @keyframes memberBorderLight {
                  0% { stroke-dashoffset: 0; }
                  100% { stroke-dashoffset: -1600; }
                }
                .member-border-light {
                  animation: memberBorderLight 8s linear infinite;
                }
                @keyframes selectedGlow {
                  0%, 100% { opacity: 0.3; }
                  50% { opacity: 0.5; }
                }
                .selected-glow {
                  animation: selectedGlow 2s ease-in-out infinite;
                }
                @keyframes rotateBorder {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
                .qr-modal-container {
                  position: relative;
                  box-shadow: 0 0 40px rgba(139,92,246,0.15), 0 0 80px rgba(139,92,246,0.08);
                  overflow: hidden;
                }
                .qr-modal-container::before {
                  content: '';
                  position: absolute;
                  top: -50%;
                  left: -50%;
                  width: 200%;
                  height: 200%;
                  background: conic-gradient(
                    from 0deg,
                    transparent 0deg,
                    transparent 80deg,
                    rgba(139,92,246,0.4) 135deg,
                    rgba(255,255,255,0.7) 180deg,
                    rgba(139,92,246,0.4) 225deg,
                    transparent 280deg,
                    transparent 360deg
                  );
                  animation: rotateBorder 5s linear infinite;
                  z-index: 0;
                }
                .qr-modal-container::after {
                  content: '';
                  position: absolute;
                  top: 1px;
                  left: 1px;
                  right: 1px;
                  bottom: 1px;
                  border-radius: 15px;
                  background: linear-gradient(145deg, rgba(18,18,28,0.995) 0%, rgba(8,8,16,1) 100%);
                  z-index: 0;
                }
                .qr-modal-container > * {
                  position: relative;
                  z-index: 1;
                }
              `}</style>
              
              <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                className="relative w-full aspect-[1.586/1] cursor-pointer" 
                style={{ perspective: '1000px' }}
                onClick={() => setFlipped(!flipped)}>
                <div className="relative w-full h-full" style={{ transformStyle: 'preserve-3d' }}>
                  
                  {/* Front - Illuminated - hide when flipped */}
                  <motion.div 
                    className="absolute w-full h-full rounded-xl p-5 overflow-hidden"
                    animate={{ 
                      rotateY: flipped ? 180 : 0,
                      opacity: flipped ? 0 : 1,
                    }}
                    transition={{ duration: 0.5 }}
                    style={{ 
                      background: 'linear-gradient(145deg, rgba(10,10,15,0.99) 0%, rgba(5,5,10,0.99) 100%)',
                      boxShadow: `0 0 30px ${getCategoryColor(member.cat)}40, inset 0 1px 1px rgba(255,255,255,0.1)`,
                      visibility: flipped ? 'hidden' : 'visible',
                    }}>
                    
                    {/* Animated border light effect */}
                    <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 400 252" preserveAspectRatio="none">
                      <defs>
                        <linearGradient id="memberCardGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="rgba(255,255,255,0)" />
                          <stop offset="40%" stopColor={`${getCategoryColor(member.cat)}99`} />
                          <stop offset="50%" stopColor="rgba(255,255,255,0.9)" />
                          <stop offset="60%" stopColor={`${getCategoryColor(member.cat)}99`} />
                          <stop offset="100%" stopColor="rgba(255,255,255,0)" />
                        </linearGradient>
                      </defs>
                      <rect
                        x="1" y="1" width="398" height="250" rx="12" ry="12"
                        fill="none"
                        stroke="url(#memberCardGrad)"
                        strokeWidth="2"
                        strokeDasharray="1400"
                        className="member-border-light"
                      />
                    </svg>
                    
                    {/* Glow overlay */}
                    <div 
                      className="absolute inset-0 rounded-xl selected-glow pointer-events-none"
                      style={{ 
                        background: `radial-gradient(ellipse at center, ${getCategoryColor(member.cat)}15 0%, transparent 70%)`,
                      }}
                    />

                    {/* Header - Logo + Category */}
                    <div className="relative z-10 flex justify-between items-start mb-4">
                      <div className="flex items-center gap-2">
                        <img src="/w-logo-chrome.png" alt="W" className="w-10 h-10 object-contain" style={{ filter: `drop-shadow(0 0 8px ${getCategoryColor(member.cat)}60)` }} />
                        <div className="font-['Jost'] text-xs tracking-[0.2em]" style={{ color: getCategoryColor(member.cat), textShadow: `0 0 10px ${getCategoryColor(member.cat)}` }}>WCLUB</div>
                      </div>
                      <div className="text-[10px] px-3 py-1 rounded-full border uppercase font-semibold bg-white/5"
                        style={{ 
                          borderColor: getCategoryColor(member.cat), 
                          color: getCategoryColor(member.cat),
                          boxShadow: `0 0 10px ${getCategoryColor(member.cat)}40`
                        }}>{member.cat}</div>
                    </div>

                    {/* Name + Number */}
                    <div className="relative z-10 space-y-1">
                      <div className="text-base font-semibold font-['Jost'] uppercase text-white" style={{ textShadow: '0 0 20px rgba(255,255,255,0.3)' }}>{member.name}</div>
                      <div className="font-['Space_Mono'] text-sm tracking-wider text-white/90">
                        {`4512 8800 9000 ${member.id.slice(-4).padStart(4, '0')}`}
                      </div>
                    </div>

                    {/* Footer - Dates */}
                    <div className="absolute bottom-5 left-5 right-5 flex justify-between text-[10px] text-white/60 z-10">
                      <span>SINCE: {member.start}</span>
                      <span>EXP: {member.valid}</span>
                    </div>
                  </motion.div>
                  
                  {/* Back - Solid background - show when flipped */}
                  <motion.div 
                    className="absolute w-full h-full rounded-xl overflow-hidden bg-black"
                    animate={{ 
                      rotateY: flipped ? 0 : -180,
                      opacity: flipped ? 1 : 0,
                    }}
                    transition={{ duration: 0.5 }}
                    style={{ 
                      visibility: flipped ? 'visible' : 'hidden',
                    }}>
                    
                    {/* Border */}
                    <div 
                      className="absolute inset-0 rounded-xl border pointer-events-none"
                      style={{ borderColor: getCategoryColor(member.cat) }}
                    />
                    
                    <div className="relative z-10 flex flex-col items-center justify-center h-full px-3 py-2">
                      {/* QR Code - Pequeno, clicável para expandir */}
                      <button
                        onClick={(e) => { e.stopPropagation(); setShowQRModal(true); }}
                        className="bg-white p-1 rounded-md active:scale-95 transition-transform"
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                      >
                        <img 
                          src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=WCLUB:${member.id}&margin=0&format=png`} 
                          alt="QR Code" 
                          className="w-12 h-12"
                        />
                      </button>
                      <p className="text-center text-white/25 text-[8px] mt-0.5">Toque para expandir</p>
                      
                      {/* Wcash Balance with visibility toggle */}
                      <div className="mt-2">
                        <div className="flex items-center justify-center gap-1">
                          <p className="text-violet-400/50 text-[9px]">Saldo Wcash</p>
                          <button
                            onClick={(e) => { e.stopPropagation(); setShowWcashBalance(!showWcashBalance); }}
                            className="p-0.5 rounded-full"
                            style={{ 
                              WebkitTapHighlightColor: 'transparent',
                              outline: 'none',
                              background: 'transparent'
                            }}
                          >
                            {showWcashBalance ? (
                              <svg className="w-2.5 h-2.5 text-violet-400/60" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                            ) : (
                              <svg className="w-2.5 h-2.5 text-violet-400/40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                              </svg>
                            )}
                          </button>
                        </div>
                        <p className="text-violet-400 text-sm font-light text-center">
                          {showWcashBalance 
                            ? `R$ ${wcashBalance !== null ? wcashBalance.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0,00'}`
                            : 'R$ ••••••'
                          }
                        </p>
                      </div>
                      
                      <p className="text-center text-white/15 text-[7px] mt-1">Toque para voltar</p>
                    </div>
                  </motion.div>
                </div>
              </motion.div>
              <p className="text-center text-white/20 mt-4 text-[10px] font-['Jost']">Toque para virar e ver QRCode e saldo Wcash</p>
              
              {/* QR Code Expanded Modal - Liquid Glass Style */}
              <AnimatePresence>
                {showQRModal && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[100] flex items-center justify-center"
                    style={{ backgroundColor: 'rgba(0,0,0,0.92)' }}
                    onClick={() => setShowQRModal(false)}
                  >
                    {/* Liquid Glass Container with animated border */}
                    <motion.div
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0.8, opacity: 0 }}
                      transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                      onClick={(e) => e.stopPropagation()}
                      className="relative p-6 rounded-2xl qr-modal-container"
                      style={{
                        background: 'linear-gradient(145deg, rgba(18,18,28,0.98) 0%, rgba(8,8,16,0.99) 100%)',
                        backdropFilter: 'blur(24px)',
                      }}
                    >
                      {/* QR Code Large */}
                      <div className="relative bg-white p-3 rounded-xl shadow-2xl">
                        <img 
                          src={`https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=WCLUB:${member.id}&margin=0&format=png`} 
                          alt="QR Code" 
                          className="w-48 h-48"
                        />
                      </div>
                      
                      {/* Member Name */}
                      <p className="text-center text-white/80 text-sm mt-4 font-['Jost'] tracking-wide">
                        {member.name}
                      </p>
                      
                      {/* Hint text */}
                      <p className="text-center text-white/30 text-[10px] mt-2">
                        Escaneie para verificar
                      </p>
                      
                      {/* Close hint */}
                      <p className="text-center text-white/20 text-[9px] mt-4">
                        Toque fora para fechar
                      </p>
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>
              
              {/* 9 Icon Menu Grid */}
              <div className="mt-8 px-2">
                <div className="grid grid-cols-3 gap-3">
                  {tabs.filter(t => t.id !== 'card').map((tab, index) => (
                    <motion.button
                      key={tab.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      onClick={() => setActiveTab(tab.id)}
                      className="relative flex flex-col items-center justify-center py-4 px-2 transition-all duration-300 touch-manipulation"
                      data-testid={`menu-${tab.id}`}
                    >
                      <MenuIcon icon={tab.icon} isActive={activeTab === tab.id} />
                      <span className={`mt-2 text-[10px] font-['Jost'] tracking-wide transition-all ${
                        activeTab === tab.id ? 'text-blue-400' : 'text-white/70'
                      }`}
                      style={{ textShadow: activeTab === tab.id ? '0 0 10px rgba(59,130,246,0.6)' : 'none' }}>
                        {tab.label}
                      </span>
                    </motion.button>
                  ))}
                </div>
              </div>
            </div>
          )}
          {activeTab === 'wia' && <WIA currentMember={member} />}
          {activeTab === 'events' && <EventsSection currentMember={member} />}
          {activeTab === 'chat' && <ChatSection currentMember={member} />}
          {activeTab === 'wcash' && <Wcash currentMember={member} />}
          {activeTab === 'payments' && <PaymentsSection currentMember={member} />}
          {activeTab === 'wbusiness' && <WBusiness members={[member]} currentMember={member} />}
          {activeTab === 'wpartners' && <WPartners currentMember={member} isAdmin={false} />}
          {activeTab === 'config' && (
            <div className="max-w-sm mx-auto px-4 py-8">
              <h2 className="text-white/80 text-lg font-['Jost'] mb-6">Configurações</h2>
              <div className="space-y-3">
                <div className="p-4 rounded-xl bg-white/[0.03] border border-white/[0.06]">
                  <p className="text-white/50 text-sm">Notificações</p>
                  <p className="text-white/30 text-xs mt-1">Gerenciar preferências de notificação</p>
                </div>
                <div className="p-4 rounded-xl bg-white/[0.03] border border-white/[0.06]">
                  <p className="text-white/50 text-sm">Privacidade</p>
                  <p className="text-white/30 text-xs mt-1">Controles de visibilidade</p>
                </div>
                <div className="p-4 rounded-xl bg-white/[0.03] border border-white/[0.06]">
                  <p className="text-white/50 text-sm">Sobre</p>
                  <p className="text-white/30 text-xs mt-1">WClub v2.0</p>
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default MemberView;
